<script setup>
import { onMounted, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import cortexSettingsAPI from 'dashboard/api/cortexSettings';
import NextButton from 'dashboard/components-next/button/Button.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import SectionLayout from '../../settings/account/components/SectionLayout.vue';

const { t } = useI18n();
const isLoading = ref(false);
const isSaving = ref(false);
const promptDefaults = ref({});
const promptResetDialog = ref(null);
const promptKeyPendingReset = ref(null);
const prompts = reactive({
  text_curation_prompt: '',
  audio_transcription_formatting_prompt: '',
  image_conversion_prompt: '',
  video_conversion_prompt: '',
  document_conversion_prompt: '',
});
const promptFields = Object.keys(prompts);

const labelKey = key =>
  `GENERAL_SETTINGS.FORM.CORTEX_CONVERSION.MEDIA_PROCESSING_PROMPTS.${key.toUpperCase()}`;

const fetchSettings = async () => {
  isLoading.value = true;
  try {
    const { data } = await cortexSettingsAPI.get();
    Object.assign(prompts, data.conversion || {});
    promptDefaults.value = data.conversion_prompt_defaults || {};
  } catch (error) {
    useAlert(t('GENERAL_SETTINGS.FORM.CORTEX_CONVERSION.API.ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const saveSettings = async () => {
  isSaving.value = true;
  try {
    const { data } = await cortexSettingsAPI.update({ conversion: prompts });
    Object.assign(prompts, data.conversion || {});
    promptDefaults.value =
      data.conversion_prompt_defaults || promptDefaults.value;
    useAlert(t('GENERAL_SETTINGS.FORM.CORTEX_CONVERSION.API.SUCCESS'));
  } catch (error) {
    useAlert(t('GENERAL_SETTINGS.FORM.CORTEX_CONVERSION.API.ERROR'));
  } finally {
    isSaving.value = false;
  }
};

const openPromptReset = key => {
  promptKeyPendingReset.value = key;
  promptResetDialog.value?.open();
};
const resetPrompt = () => {
  const key = promptKeyPendingReset.value;
  if (key) prompts[key] = promptDefaults.value[key];
  promptResetDialog.value?.close();
  promptKeyPendingReset.value = null;
};
const closePromptReset = () => {
  promptKeyPendingReset.value = null;
};

onMounted(fetchSettings);
</script>

<template>
  <SectionLayout
    :title="t('CORTEX.SETTINGS_GUIDE.PROMPTS.TITLE')"
    :description="t('CORTEX.SETTINGS_GUIDE.PROMPTS.DESCRIPTION')"
    with-border
  >
    <woot-loading-state v-if="isLoading" />
    <form v-else class="grid gap-4" @submit.prevent="saveSettings">
      <div v-for="key in promptFields" :key="key" class="grid gap-2">
        <TextArea
          v-model="prompts[key]"
          :label="t(`${labelKey(key)}.LABEL`)"
          :message="t(`${labelKey(key)}.HELP`)"
          :placeholder="t(`${labelKey(key)}.PLACEHOLDER`)"
          :max-length="2000"
          show-character-count
          auto-height
          resize
        />
        <div class="flex justify-end">
          <NextButton
            type="button"
            sm
            variant="faded"
            color="slate"
            icon="i-lucide-rotate-ccw"
            :label="
              t(
                'GENERAL_SETTINGS.FORM.CORTEX_CONVERSION.MEDIA_PROCESSING_PROMPTS.RESET'
              )
            "
            @click="openPromptReset(key)"
          />
        </div>
      </div>
      <NextButton type="submit" icon="i-lucide-save" :is-loading="isSaving">
        {{ t('GENERAL_SETTINGS.FORM.CORTEX_CONVERSION.SUBMIT') }}
      </NextButton>
    </form>
    <Dialog
      ref="promptResetDialog"
      type="alert"
      width="md"
      :title="
        t(
          'GENERAL_SETTINGS.FORM.CORTEX_CONVERSION.MEDIA_PROCESSING_PROMPTS.RESET_DIALOG.TITLE'
        )
      "
      :description="
        t(
          'GENERAL_SETTINGS.FORM.CORTEX_CONVERSION.MEDIA_PROCESSING_PROMPTS.RESET_DIALOG.DESCRIPTION',
          {
            prompt: promptKeyPendingReset
              ? t(`${labelKey(promptKeyPendingReset)}.LABEL`)
              : '',
          }
        )
      "
      :cancel-button-label="
        t(
          'GENERAL_SETTINGS.FORM.CORTEX_CONVERSION.MEDIA_PROCESSING_PROMPTS.RESET_DIALOG.CANCEL'
        )
      "
      :confirm-button-label="
        t(
          'GENERAL_SETTINGS.FORM.CORTEX_CONVERSION.MEDIA_PROCESSING_PROMPTS.RESET_DIALOG.CONFIRM'
        )
      "
      confirm-button-icon="i-lucide-rotate-ccw"
      @close="closePromptReset"
      @confirm="resetPrompt"
    />
  </SectionLayout>
</template>
